import dayjs from 'dayjs';
import { FormikErrors } from 'formik';
import { isBoolean } from 'lodash';
import { NContact } from '../Contact/validation';
import {
  IAsset,
  IFormAsset,
  VALUATION_TYPES,
  PRODUCT_TYPES,
  IAssetLienHolder,
  IAssetValuation,
  ICurrentProduct,
  ILienHolder,
  GenericFormikErrrorVals,
} from './interfaces';

const YES = 'YES';
const NO = 'NO';

export const getAssetOwnerValue = (isAssetOwner?: string | null) => {
    if (!isAssetOwner) return null;
    if (isAssetOwner === YES || isAssetOwner === NO) {
      return isAssetOwner.charAt(0).toUpperCase() + isAssetOwner.slice(1).toLowerCase();
    } 
};

export const setAssetOwnerValue = (isAssetOwner?: string | null) => {
  return isAssetOwner ? isAssetOwner.toUpperCase() : null;
};

export const getAssetFormModel = (asset: IAsset, contact?: NContact.Entity) => {
  const address: NContact.Address =
    contact?.addresses && contact.addresses.length > 0 ? contact.addresses[0] : ({} as NContact.Address);

  const assetFormModel: IFormAsset = {
    ...asset,
    isAssetOwner: getAssetOwnerValue(asset?.isAssetOwner),
    contactId: contact?.id,
    assetType: asset.assetType || 'Car',
    assetCondition: asset.assetCondition || 'Used',
    mileage: asset.mileage ?? null,
    vin: asset.vin || '',
    year: asset.year || 0,
    make: asset.make || '',
    model: asset.model || '',
    kbbTrim: asset.kbbTrim || '',
    nadaTrim: asset.nadaTrim || '',
    color: asset.color || '',
    isPlateToVinSuccess: asset.isPlateToVinSuccess || false,
    isVinLookupSuccess: false,
    assetRegistration: {
      ...asset.assetRegistration,
      registeredOwners: (asset.assetRegistration?.primaryOwner ? 1 : 0) + (asset.assetRegistration?.secondaryOwner ? 1 : 0),
      primaryOwner:
        asset.assetRegistration?.primaryOwner || `${contact?.firstName} ${contact?.middleInitial ?? ''} ${contact?.lastName} `,
      state: asset.assetRegistration?.state || address.state || '',
      secondaryOwner: asset.assetRegistration?.secondaryOwner || '',
    },
    assetLienHolder: {
      ...asset.assetLienHolder,
      accountNumber: asset.assetLienHolder?.accountNumber || '',
      lienHolderId: asset.assetLienHolder?.lienHolderId || null,
      phoneNumber: asset.assetLienHolder?.phoneNumber || '',
      loan: {
        ...asset?.assetLienHolder?.loan,
        loanAmount: asset?.assetLienHolder?.loan?.loanAmount ?? null,
        rate: asset?.assetLienHolder?.loan?.rate ?? null,
        monthlyPayment: asset?.assetLienHolder?.loan?.monthlyPayment ?? null,
        perDiem: asset?.assetLienHolder?.loan?.perDiem ?? null,
        term: asset?.assetLienHolder?.loan?.term ?? null,
      },
      address: {
        streetLine1: asset.assetLienHolder?.address?.streetLine1 || '',
        streetLine2: asset.assetLienHolder?.address?.streetLine2 || '',
        city: asset.assetLienHolder?.address?.city || '',
        state: asset.assetLienHolder?.address?.state || null,
        zip: asset.assetLienHolder?.address?.zip || '',
      },
    },
    kbb: {
      type: VALUATION_TYPES.Kbb,
      baseWholesaleAmount: '',
      baseRetailAmount: '',
      baseFinanceExpressAmount: '',
    },
    nada: {
      type: VALUATION_TYPES.Nada,
      baseTradeInAmount: '',
      baseRetailAmount: '',
    },
    assetInsurance: {
      company: asset.assetInsurance?.company || '', 
      policyNumber: asset.assetInsurance?.policyNumber || '',
      phoneNumber: asset.assetInsurance?.phoneNumber || '',
      effectiveDate: asset.assetInsurance?.effectiveDate || null,
      expirationDate: asset.assetInsurance?.expirationDate || null,
    },
    assetBrand: {
      brandedStateList: asset.assetBrand?.brandedStateList || [],
      brandName: asset.assetBrand?.brandName || '',
      brandDescription: asset.assetBrand?.brandDescription || '',
    },
    assetValidations: {
      isLiftKit: isBoolean(asset?.assetValidations?.isLiftKit) ? (asset?.assetValidations.isLiftKit ? 'Yes' : 'No') : null,
      isSixInchLiftKit: isBoolean(asset?.assetValidations?.isSixInchLiftKit)  ? (asset?.assetValidations.isSixInchLiftKit ? 'Yes' : 'No') : null,
      isHEV: isBoolean(asset?.assetValidations?.isHEV) ? (asset?.assetValidations.isHEV ? 'Yes' : 'No') : null,
      doesOversizeUndersizeTire: isBoolean(asset?.assetValidations?.doesOversizeUndersizeTire) ? (asset?.assetValidations.doesOversizeUndersizeTire ? 'Yes': 'No') : null,
    },
  } as IFormAsset;

  if (asset && asset.id) {
    const { assetValuations, assetLienHolder } = assetFormModel;

    if (assetValuations && assetValuations.length) {
      assetFormModel.kbb = assetValuations.find((av) => av.type === VALUATION_TYPES.Kbb);
      assetFormModel.nada = assetValuations.find((av) => av.type === VALUATION_TYPES.Nada);
      assetFormModel.bumper = assetValuations.find((av) => av.type === VALUATION_TYPES.Bumper);
    }

    if (assetLienHolder && assetLienHolder.currentProducts && assetLienHolder.currentProducts.length) {
      assetFormModel.vscRefund = (
        assetLienHolder.currentProducts.find((cp) => cp.productType === PRODUCT_TYPES.Vsc) || {}
      ).refundAmount;
      assetFormModel.gapRefund = (
        assetLienHolder.currentProducts.find((cp) => cp.productType === PRODUCT_TYPES.Gap) || {}
      ).refundAmount;
    }
  }

  return assetFormModel;
};

export const getAssetRequestFormModel = (data: IFormAsset, contactIdParam: string, lienHolders?: ILienHolder[]) => {
  const postData: IFormAsset = JSON.parse(JSON.stringify(data));
  let existingLienHolder;
  //setting currentProducts in line holder
  const products: ICurrentProduct[] = [];
  if (data.vscRefund) {
    products.push({ productType: PRODUCT_TYPES.Vsc, refundAmount: data.vscRefund });
  }
  if (data.gapRefund) {
    products.push({ productType: PRODUCT_TYPES.Gap, refundAmount: data.gapRefund });
  }

  const lienHolder: IAssetLienHolder = JSON.parse(JSON.stringify(data.assetLienHolder || ({} as IAssetLienHolder)));
  lienHolder.currentProducts = products;

  if (lienHolder.name === lienHolder.lienHolderId) {
    lienHolder.lienHolderId = null;
  } else if (!lienHolder.acceleratedTitlePartnerCode) {
     existingLienHolder = lienHolders?.find((lh) => lh.id === lienHolder.lienHolderId);

    if (!existingLienHolder) {
      const acceleratedTitlePartner = lienHolders?.find((lh) => lh.acceleratedTitlePartnerCode === lienHolder.lienHolderId);
      if (acceleratedTitlePartner) {
        lienHolder.acceleratedTitlePartnerCode = acceleratedTitlePartner.acceleratedTitlePartnerCode;
      } else if (!lienHolder.name) {
        lienHolder.name = lienHolder.lienHolderId ?? '';
      }
      lienHolder.lienHolderId = null;
    }
  }

  if (postData?.assetLienHolder) {
    postData.assetLienHolder.isAcceleratedTitleSupported = postData.assetLienHolder.isAcceleratedTitleSupported ?? false;
    postData.assetLienHolder.acceleratedTitleTitleIsReleasable =  postData.assetLienHolder.acceleratedTitleTitleIsReleasable ?? false;
    postData.assetLienHolder.acceleratedTitleTransactionCharges =  postData.assetLienHolder.acceleratedTitleTransactionCharges ?? 0;
    postData.assetLienHolder.acceleratedTitleTaxFee =  postData.assetLienHolder.acceleratedTitleTaxFee ?? 0;
    postData.assetLienHolder.goodDate = dayjs(postData.assetLienHolder.goodDate).isValid()
      ? dayjs(postData.assetLienHolder.goodDate).format()
      : null;
    postData.assetLienHolder.loan.nextPaymentDate = dayjs(postData.assetLienHolder.loan.nextPaymentDate).isValid()
      ? dayjs(postData.assetLienHolder.loan.nextPaymentDate).format()
      : null;
  }

  if (postData.mileage?.toString() === '') {
    postData.mileage = null;
  }

  if (lienHolder?.address?.state === '') {
    lienHolder.address.state = null;
  }

  if (lienHolder?.loan?.rate === '') {
    lienHolder.loan.rate = null;
  }

  if (lienHolder?.loan?.perDiem === '') {
    lienHolder.loan.perDiem = null;
  }

  if (lienHolder?.loan?.term === '') {
    lienHolder.loan.term = null;
  }

  if (lienHolder?.loan?.loanAmount === '') {
    lienHolder.loan.loanAmount = null;
  }

  if (lienHolder?.loan?.monthlyPayment === '') {
    lienHolder.loan.monthlyPayment = null;
  }

  if (isNaN(Date.parse(lienHolder?.goodDate as string))) {
    lienHolder.goodDate = null;
  }
  if (isNaN(Date.parse(lienHolder?.loan?.nextPaymentDate as string))) {
    lienHolder.loan.nextPaymentDate = null;
  }

  // setting kbb and JD Power and Bumper
  const valuations: IAssetValuation[] = [];
  if (data.kbb?.baseWholesaleAmount || data?.kbb?.baseRetailAmount || data?.kbb?.baseFinanceExpressAmount) {
    valuations.push({
      type: VALUATION_TYPES.Kbb,
      baseRetailAmount: data.kbb.baseRetailAmount || null,
      baseTradeInAmount: data.kbb.baseTradeInAmount || null,
      baseWholesaleAmount: data.kbb.baseWholesaleAmount || null,
      baseFinanceExpressAmount: data.kbb.baseFinanceExpressAmount || null,
    });
  }

  if (data.nada?.baseTradeInAmount || data?.nada?.baseRetailAmount) {
    valuations.push({
      type: VALUATION_TYPES.Nada,
      baseRetailAmount: data.nada.baseRetailAmount || null,
      baseWholesaleAmount: data.nada.baseTradeInAmount || null,
      baseTradeInAmount: data.nada.baseTradeInAmount || null,
    });
  }

  if (data.bumper?.high || data?.bumper?.avg || data?.bumper?.low) {
    valuations.push({
      type: VALUATION_TYPES.Bumper,
      high: data.bumper.high || null,
      avg: data.bumper.avg || null,
      low: data.bumper.low || null
    });
  }
  postData.assetLienHolder = lienHolder;
  postData.assetValuations = valuations;
  postData.bookOutDate = data.bookOutDate === '' ? null : data.bookOutDate;
  postData.contactId = contactIdParam;

  if (postData?.assetRegistration) {
    postData.assetRegistration.registeredOwners =
      (data.assetRegistration?.primaryOwner ? 1 : 0) + (data.assetRegistration?.secondaryOwner ? 1 : 0);
      postData.assetRegistration.expirationDate = postData.assetRegistration.expirationDate === '' ? null : postData.assetRegistration.expirationDate;
  }

  if (postData?.assetInsurance) {
      postData.assetInsurance.effectiveDate = postData.assetInsurance.effectiveDate === '' ? null : postData.assetInsurance.effectiveDate;
      postData.assetInsurance.expirationDate = postData.assetInsurance.expirationDate === '' ? null : postData.assetInsurance.expirationDate;
  }


  if (postData?.assetLienHolder && !existingLienHolder?.acceleratedTitlePartnerCode) {
    postData.assetLienHolder.isAcceleratedTitleSupported = null;
    postData.assetLienHolder.acceleratedTitleTitleIsReleasable = null;
    postData.assetLienHolder.acceleratedTitleTransactionCharges = null;
    postData.assetLienHolder.acceleratedTitleTaxFee = null;
    postData.assetLienHolder.acceleratedTitleTitleFormat = null;
    postData.assetLienHolder.acceleratedTitleTitleID = null;
    postData.assetLienHolder.acceleratedTitleTitleState = null;
  
  }
  return postData;
};

export const getAssetOptions = (assets?: IAsset[]) => {
  const options = (assets || []).map(asset => {
    const isEmptyAsset = !asset?.year && !asset?.make && !asset?.model && !asset?.vin;
    const assetName = !isEmptyAsset ? `${asset?.year ?? ''} ${asset?.make ?? ''} ${asset?.model ?? ''} ${asset?.vin?.toUpperCase() ?? ''}` : 'Asset';

    return {
      id: asset.id,
      display: assetName?.trim(),
    };
  });

  return options;
};

export const getAssetValue = (asset?: string) => {
  const value = asset?.trim();
  const assetTag = (value && value != '0') ? value : 'Asset';

  return assetTag;
};


export const extractErrorMessages = (errors: FormikErrors<GenericFormikErrrorVals>): string[] => {
  const messages: string[] = [];

  function traverseErrors(errObj: unknown) {
    if (typeof errObj === 'string') {
      messages.push(errObj);
    } else if (Array.isArray(errObj)) {
      errObj.forEach(traverseErrors);
    } else if (typeof errObj === 'object' && errObj !== null) {
      Object.values(errObj).forEach(traverseErrors);
    }
  }

  traverseErrors(errors);
  return messages;
};